import { useZero } from "@rocicorp/zero/react";
import { useState } from "react";
import type { ConnectorDefinition } from "../../../shared/connectors/types";
import { ExpectedError } from "../../../shared/expectedError";
import { sharedMutators } from "../../../shared/mutators";
import { apiUrl } from "../../lib/tenantOrigin";
import { Icon } from "../Icon";
import { IntegrationButton } from "../IntegrationButton";
import { Text } from "../Text";
import {
  AuthorizedButton,
  AuthorizedInfo,
  AuthorizedLabel,
  CheckIcon,
} from "./authorizedChip";
import type { App } from "./Form";
import { useOauthPopupWindow } from "./useOauthPopupWindow";

type Props = {
  app: App;
  definition: ConnectorDefinition;
  disabled?: boolean;
};

export function GithubAppManifestFieldItem({
  app,
  definition,
  disabled,
}: Props) {
  const popup = useOauthPopupWindow();
  const z = useZero();
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const externalApiUrl = apiUrl();

  const { appId, appSlug, installationId, organizationLogin } =
    app.configuration;
  const installed = Boolean(appId && installationId);
  // Only a manifest-created app has a slug to resume the install picker with; a
  // manually-entered app falls through to creating a new one.
  const canResumeInstall = Boolean(appId && appSlug && !installationId);
  // GitHub has no manifest field or API for app logos; uploading is manual.
  const settingsUrl = appSlug
    ? organizationLogin
      ? `https://github.com/organizations/${encodeURIComponent(organizationLogin)}/settings/apps/${encodeURIComponent(appSlug)}`
      : `https://github.com/settings/apps/${encodeURIComponent(appSlug)}`
    : null;

  const start = async () => {
    setIsPending(true);
    setError(null);
    try {
      let url: string;
      if (canResumeInstall) {
        url = `https://github.com/apps/${encodeURIComponent(appSlug!)}/installations/new`;
      } else {
        const startUrl = new URL(
          `${externalApiUrl}/connectors/github-app/manifest/start`
        );
        startUrl.searchParams.set("app_id", app.id);
        url = startUrl.toString();
      }
      const result = await popup.flow(url, new URL(externalApiUrl).origin);
      if (result && "error" in result) setError(result.error);
      if (result && "success" in result && result.installationId)
        try {
          z.mutate(
            sharedMutators.recordGithubAppInstallation({
              connectedAppId: app.id,
              installationId: result.installationId,
            })
          );
        } catch (mutateError) {
          if (!(mutateError instanceof ExpectedError)) {
            console.error(
              "Recording GitHub App installation failed:",
              mutateError
            );
          }
          setError(
            mutateError instanceof Error
              ? mutateError.message
              : "Recording the GitHub App installation failed."
          );
        }
    } finally {
      setIsPending(false);
    }
  };

  if (installed) {
    return (
      <div>
        <AuthorizedButton style={{ padding: "6px 20px" }}>
          <AuthorizedLabel>
            <img src={definition.logo} alt={definition.name} />
            <Text variant="text-regular-md">GitHub App installed</Text>
          </AuthorizedLabel>
        </AuthorizedButton>
        <AuthorizedInfo>
          <CheckIcon />
          <Text variant="text-small" color="var(--text-secondary)">
            {appSlug ? `github.com/apps/${appSlug}` : `App ID ${appId}`}
          </Text>
        </AuthorizedInfo>
        {settingsUrl && (
          <AuthorizedInfo>
            <Text variant="text-small" color="var(--text-secondary)">
              Optionally,{" "}
              <a href={settingsUrl} target="_blank" rel="noopener noreferrer">
                upload a logo
              </a>{" "}
              in the app’s GitHub settings.
            </Text>
          </AuthorizedInfo>
        )}
      </div>
    );
  }

  return (
    <div>
      <IntegrationButton
        type="button"
        onClick={start}
        disabled={disabled}
        isLoading={isPending}
        logo={definition.logo}
        text={canResumeInstall ? "Install GitHub App" : "Create GitHub App"}
      />
      {error && (
        <AuthorizedInfo>
          <Icon name="info" size={12} />
          <Text variant="text-small" color="var(--text-error)">
            {error}
          </Text>
        </AuthorizedInfo>
      )}
    </div>
  );
}
